import type { IOrder } from '@wyz/types';
import { Button, Icon, OverlayTrigger, Popover, Text, Tooltip } from '@wyz/ui';
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import { Trans } from '~/common/context';
// eslint-disable-next-line @wyz/module/no-relative-import
import './tracking-cell.scss';

type Props = {
  order: IOrder;
};

const OrderTrackingCell = ({ order }: Props) => {
  const trackings = order.trackings ?? [];

  if (trackings.length === 0) {
    return <>-</>;
  }

  const packageCount = (
    <Trans
      i18nKey='app.purchase.order_detail.tracking.n_package'
      values={{ count: trackings.length }}
    />
  );

  if (trackings.length === 1) {
    const url = trackings[0].url;

    return (
      <Tooltip
        label={
          <Trans i18nKey='app.purchase.orders.table.tracking_link_hover' />
        }
        placement='bottom'
      >
        <Button
          as='a'
          id={`${order.orderReference}-open-tracking`}
          variant='tertiary-blue'
          size='xs'
          href={url}
          target='_blank'
          rel='noreferrer'
          disabled={isNil(url)}
        >
          {packageCount}
          <Icon
            icon='share-box'
            size='xxs'
            className='order-tracking-external-icon ms-1'
          />
        </Button>
      </Tooltip>
    );
  }

  return (
    <OverlayTrigger
      trigger='click'
      rootClose
      placement='bottom'
      popperConfig={{ strategy: 'fixed' }}
      overlay={
        <Popover className='py-2 px-3'>
          <table>
            <tbody>
              {trackings.map((tracking, index) => (
                <tr key={index}>
                  <td className='align-middle py-1 pe-3'>
                    <Text
                      color='secondary-gray'
                      heading='xs'
                      weight='semi-bold'
                    >
                      <Trans i18nKey='app.purchase.order_detail.tracking.package' />{' '}
                      {index + 1}
                    </Text>
                  </td>
                  <td className='align-middle py-1'>
                    <Text
                      component='a'
                      id={`${order.orderReference}-tracking-${index}`}
                      href={tracking.url}
                      target='_blank'
                      rel='noreferrer'
                      color='tertiary'
                      heading='sm'
                      weight='bold'
                      className={clsx('d-flex align-items-center', {
                        'disabled-link': isNil(tracking.url),
                      })}
                    >
                      <Trans i18nKey='app.purchase.orders.table.tracking' />
                      <Icon
                        icon='share-box'
                        size='xxs'
                        className='order-tracking-external-icon ms-1'
                      />
                    </Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Popover>
      }
    >
      <Button
        id={`${order.orderReference}-open-tracking`}
        variant='tertiary-blue'
        size='xs'
      >
        <Icon icon='box-line' size='xs' className='me-1' />
        {packageCount}
      </Button>
    </OverlayTrigger>
  );
};

export default OrderTrackingCell;
